import { type ByProjectKeyRequestBuilder, createApiBuilderFromCtpClient } from '@commercetools/platform-sdk';
import {
    type AuthMiddlewareOptions,
    ClientBuilder,
    type HttpMiddlewareOptions,
    type Middleware,
} from '@commercetools/sdk-client-v2';
import { trace } from '@opentelemetry/api';
import fetch from 'node-fetch';
import { v4 as uuidv4 } from 'uuid';

import { customObjectApi } from '../lib/api/customObject';
import { ordersApi } from '../lib/api/orders';
import { patchesApi } from '../lib/api/patches';
import { productApi } from '../lib/api/product';
import { productDiscountApi } from '../lib/api/product-discount';
import { shippingApi } from '../lib/api/shipping-method';
import { storeApi } from '../lib/api/store';
import { taxApi } from '../lib/api/tax';
import { typeApi } from '../lib/api/type';
import { zoneApi } from '../lib/api/zone';
import { singleton } from '../lib/utils/singleton';
import type { ApiClientDetails, ClientConfig } from '../types';

// Optional logger import - falls back to console when @libs/core is not available (e.g., in vcli context)
// Lazy initialization ensures require() only executes once and is cached by Node.js
let logger: { info: (message: string) => void } | undefined;
const getLogger = (): { info: (message: string) => void } => {
    if (!logger) {
        try {
            const coreModule = require('@libs/core');
            logger = coreModule.logger;
        } catch {
            logger = { info: (message: string) => console.info(message) };
        }
    }
    return logger as { info: (message: string) => void };
};

const formatCorrelationId = (origin: string, rootId: string, spanId: string): string => `${origin}_${rootId}_${spanId}`;

/**
 * Transforms APP environment variable to shortened form.
 * Takes the first character of each hyphen-separated part and converts to lowercase.
 * Examples: "customer-web-experience-api" → "cwea", "orders-api" → "oa", "account-process-api" → "apa"
 * @returns Transformed app name or 'cli' if APP is not set
 */
const getAppShortName = (): string =>
    process.env.APP?.split('-')
        .map(part => part.charAt(0).toLowerCase())
        .join('') ?? 'cli';

const createCorrelationHeaderMiddleware = (): Middleware => {
    return next => (request, response) => {
        const origin = getAppShortName();
        const rootId = trace.getActiveSpan()?.spanContext().traceId ?? uuidv4();
        const spanId = uuidv4();
        const headerValue = formatCorrelationId(origin, rootId, spanId);

        getLogger().info(`[commerceToolsClient] x-correlation-id: ${headerValue}`);

        request.headers = {
            ...(request.headers || {}),
            'x-correlation-id': headerValue,
        } as Record<string, string>;

        return next(request, response);
    };
};

export function createCtClient() {
    const {
        COMMERCETOOLS_PROJECT_KEY,
        COMMERCETOOLS_TOKEN_URL,
        COMMERCETOOLS_CLIENT_ID,
        COMMERCETOOLS_CLIENT_SECRET,
        COMMERCETOOLS_API_URL,
    } = process.env;

    if (!COMMERCETOOLS_PROJECT_KEY) {
        throw new Error(`
        ERROR: The environment variable "COMMERCETOOLS_PROJECT_KEY" is not set in
        the .env file located at the root of the project.
    `);
    }

    if (!COMMERCETOOLS_TOKEN_URL) {
        throw new Error(`
        ERROR: The environment variable "COMMERCETOOLS_TOKEN_URL" is
        not set in the .env file located at the root of the project.
    `);
    }

    if (!COMMERCETOOLS_CLIENT_ID) {
        throw new Error(`
        ERROR: The environment variable "COMMERCETOOLS_CLIENT_ID" is
        not set in the .env file located at the root of the project.
    `);
    }

    if (!COMMERCETOOLS_CLIENT_SECRET) {
        throw new Error(`
        ERROR: The environment variable "COMMERCETOOLS_CLIENT_SECRET" is
        not set in the .env file located at the root of the project.
    `);
    }

    if (!COMMERCETOOLS_API_URL) {
        throw new Error(`
        ERROR: The environment variable "COMMERCETOOLS_API_URL" is
        not set in the .env file located at the root of the project.
    `);
    }

    const scopes = [`manage_project:${COMMERCETOOLS_PROJECT_KEY}`];
    const authMiddlewareOptions: AuthMiddlewareOptions = {
        host: COMMERCETOOLS_TOKEN_URL,
        projectKey: COMMERCETOOLS_PROJECT_KEY,
        credentials: {
            clientId: COMMERCETOOLS_CLIENT_ID,
            clientSecret: COMMERCETOOLS_CLIENT_SECRET,
        },
        scopes,
        fetch,
    };

    const timeoutOptions = process.env.COMMERCETOOLS_CLIENT_TIMEOUT_SECONDS
        ? {
              timeout: Number(process.env.COMMERCETOOLS_CLIENT_TIMEOUT_SECONDS) * 1000,
              getAbortController: () => new AbortController(),
          }
        : {};

    const httpMiddlewareOptions: HttpMiddlewareOptions = {
        host: COMMERCETOOLS_API_URL,
        fetch,
        ...timeoutOptions,
    };

    if (process.env.COMMERCETOOLS_CLIENT_TIMEOUT_SECONDS) {
        getLogger().info(
            `[commerceToolsClient] using COMMERCETOOLS_CLIENT_TIMEOUT_SECONDS: ${process.env.COMMERCETOOLS_CLIENT_TIMEOUT_SECONDS}`
        );
    }

    const ctpClient = singleton(() =>
        new ClientBuilder()
            .withProjectKey(COMMERCETOOLS_PROJECT_KEY)
            .withClientCredentialsFlow(authMiddlewareOptions)
            .withHttpMiddleware(httpMiddlewareOptions)
            .withMiddleware(createCorrelationHeaderMiddleware())
            .build()
    );

    const apiRoot = singleton(() =>
        createApiBuilderFromCtpClient(ctpClient()).withProjectKey({
            projectKey: COMMERCETOOLS_PROJECT_KEY,
        })
    );

    const clientConfig: ClientConfig = {
        projectKey: COMMERCETOOLS_PROJECT_KEY,
        clientId: COMMERCETOOLS_CLIENT_ID,
        clientSecret: COMMERCETOOLS_CLIENT_SECRET,
        authUrl: COMMERCETOOLS_TOKEN_URL,
        apiUrl: COMMERCETOOLS_API_URL,
    };

    const apiClient: ApiClientDetails = {
        ctpClient,
        apiRoot,
        clientConfig,
    };

    return {
        ctpClient,
        apiRoot,
        clientConfig,
        ...customObjectApi(apiClient),
        ...taxApi(apiClient),
        ...typeApi(apiClient),
        ...patchesApi(apiClient),
        ...storeApi(apiClient),
        ...productDiscountApi(apiClient),
        ...zoneApi(apiClient),
        ...productApi(apiClient),
        ...shippingApi(apiClient),
        ...ordersApi(apiClient),
    };
}

export type CtApiClient = ReturnType<typeof createCtClient>;

export const getApiClient = singleton((): ByProjectKeyRequestBuilder => createCtClient().apiRoot());
